
import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Standardized page header component for Fuel.AI platform
 */
const PageHeader = ({
  pageTitle,
  mainTitle,
  description,
  filters,
  tabs,
  activeTab,
  setActiveTab,
  showBackButton = false,
  backTo = '/'
}) => {
  return (
    <div className="page-header bg-white border-b border-gray-200 py-4 px-6 shadow-sm">
      {/* Top bar with platform name and page identifier */}
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center">
          <Link to="/" className="text-xl font-medium text-purple-900">
            Fuel.AI
          </Link>
          {pageTitle && (
            <div className="ml-3 px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-sm font-medium">
              {pageTitle}
            </div>
          )}
        </div>
      </div>

      {/* Back button and filter controls */}
      {(showBackButton || filters) && (
        <div className="flex items-center mb-6">
          {showBackButton && (
            <Link to={backTo} className="mr-4 bg-white rounded-full p-2 shadow-sm border border-gray-200 hover:bg-gray-50">
              <ArrowLeft size={20} className="text-gray-600" />
            </Link>
          )}

          {/* Page-specific filters - render conditionally */}
          {filters && (
            <div className="flex gap-3">
              {filters.map((filter) => (
                <div key={filter.name} className="filter-control">
                  <div className="flex items-center">
                    {filter.icon && (
                      <span className="text-purple-600 mr-1">{filter.icon}</span>
                    )}
                    <div className="relative">
                      <select
                        className="appearance-none bg-white border border-gray-200 rounded-lg py-2 px-4 pr-8 
                                text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-200"
                        value={filter.value}
                        onChange={filter.onChange}
                      >
                        {filter.options.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                          <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab navigation - render conditionally */}
      {tabs && (
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 text-sm font-medium border-b-2 ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Main page title */}
      {mainTitle && (
        <div className="mb-2">
          <h2 className="text-xl font-medium text-gray-900">{mainTitle}</h2>
          {description && <p className="text-gray-600 text-sm">{description}</p>}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
